"use client";

import Link from "next/link";
import { renderTextWithLatex } from "@/components/LaTeXRenderer";

export interface PastPaperQuestion {
  questionNumber: number;
  text: string;
  options?: Record<string, string>;
  marks?: number;
  answer?: string;
  hasWorkedSolution: boolean;
  workedSolution?: string;
  // Mirrors UAT: this question is outside the current ESAT specification (the
  // advanced-physics items UAT crosses out in its ESAT-prep papers).
  outOfSpec?: boolean;
}

export function PastPaperQuestions({
  questions,
  locked,
  ctaHref,
}: {
  questions: PastPaperQuestion[];
  locked: boolean;
  ctaHref: string;
}) {
  return (
    <div className="mt-10 space-y-10">
      {questions.map((q) => (
        <div key={q.questionNumber} className="border-b border-[var(--border)] pb-10 last:border-b-0">
          <div className="flex items-baseline justify-between gap-3">
            <h2
              className={`text-lg font-semibold text-[var(--foreground)]${
                q.outOfSpec ? " line-through text-[var(--muted-foreground)]" : ""
              }`}
            >
              Question {q.questionNumber}
            </h2>
            {q.marks ? (
              <span className="text-xs text-[var(--muted-foreground)]">
                {q.marks} mark{q.marks > 1 ? "s" : ""}
              </span>
            ) : null}
          </div>

          {q.outOfSpec && (
            <p className="mt-2 text-xs font-medium text-[var(--muted-foreground)]">
              Not in the current ESAT specification — included for completeness, but you won&apos;t be
              tested on it.
            </p>
          )}

          <div
            className={`mt-3 leading-relaxed text-[var(--foreground)]${
              q.outOfSpec ? " opacity-60" : ""
            }`}
          >
            {renderTextWithLatex(q.text)}
          </div>

          {q.options && Object.keys(q.options).length > 0 && (
            <ul className="mt-4 space-y-2">
              {Object.entries(q.options).map(([key, value]) => (
                <li key={key} className="flex gap-3 text-[var(--foreground)]">
                  <span className="font-mono text-[var(--muted-foreground)]">{key}.</span>
                  <span>{renderTextWithLatex(value)}</span>
                </li>
              ))}
            </ul>
          )}

          {q.answer && (
            <p className="mt-4 text-sm text-[var(--foreground)]">
              <span className="font-semibold">Answer:</span> {q.answer}
            </p>
          )}

          {q.workedSolution ? (
            <div className="mt-5 rounded-lg border border-[var(--border)] bg-[var(--muted)] p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
                Worked solution
              </p>
              <div className="mt-2 leading-relaxed text-[var(--foreground)]">
                {renderTextWithLatex(q.workedSolution)}
              </div>
            </div>
          ) : q.hasWorkedSolution && locked ? (
            <div className="mt-5 rounded-lg border border-dashed border-[var(--border)] p-4">
              <p className="text-sm font-medium text-[var(--foreground)]">
                Full step-by-step worked solution
              </p>
              <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                Locked. Available with a free account.
              </p>
              <Link
                href={ctaHref}
                className="mt-3 inline-block text-sm font-medium text-[var(--primary)] hover:underline"
              >
                Unlock worked solutions →
              </Link>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
